// PresentationSheet.jsx — printable product one-pagers for store visits.
// 2026-08-21 (Brian): exactly 9 products per printed page (3×3, hard page
// breaks); per-product promo pricing for new-store pitches — % off, sale
// price, or N+M free (10+1) — shown as regular price struck + red promo;
// one "effective until" date printed small on the bottom of EVERY page.
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getAllProducts } from "../services/dataCache";
import { TOFU_ORDER } from "../utils/orderSort";
import { productMatches } from "../utils/searchMatch";

// Tofu family prints as one group in its canonical order (10001 ... 10098,
// 40891 last) even though 40891's number is far away — the SHEET follows
// the same tofu-first rule as invoices/packing sheets (Brian, 2026-08-21).
// Missing tofus just drop out; 40891 follows whichever tofus are selected.
const TOFU_INDEX = new Map(TOFU_ORDER.map((sku, i) => [sku, i]));

function sheetOrder(a, b) {
  const ta = TOFU_INDEX.has(a.sku) ? TOFU_INDEX.get(a.sku) : null;
  const tb = TOFU_INDEX.has(b.sku) ? TOFU_INDEX.get(b.sku) : null;
  if (ta !== null && tb !== null) return ta - tb;
  if (ta !== null) return -1;
  if (tb !== null) return 1;
  return skuCompare(a, b);
}

const PER_PAGE = 9; // 3 columns × 3 rows per printed page

function skuCompare(a, b) {
  const numA = parseInt(a.sku, 10);
  const numB = parseInt(b.sku, 10);
  const aIsNum = !isNaN(numA) && /^\d+/.test(a.sku);
  const bIsNum = !isNaN(numB) && /^\d+/.test(b.sku);
  if (aIsNum && bIsNum) return numA - numB;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.sku.localeCompare(b.sku);
}

function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function formatSheetDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
}

// Promo per product: { type: "pct" | "price" | "nm", pct, price, n, m }.
// Returns the computed sale price for pct/price promos (null for nm/none).
function salePrice(p, promo) {
  if (!promo) return null;
  if (promo.type === "pct") {
    const pct = parseFloat(promo.pct);
    if (!(pct > 0 && pct < 100)) return null;
    return p.basePrice * (1 - pct / 100);
  }
  if (promo.type === "price") {
    const v = parseFloat(promo.price);
    if (!(v > 0)) return null;
    return v;
  }
  return null;
}

function nmLabel(promo) {
  if (!promo || promo.type !== "nm") return null;
  const n = parseInt(promo.n, 10);
  const m = parseInt(promo.m, 10);
  if (!(n > 0 && m > 0)) return null;
  return `${n}+${m} FREE`;
}

export default function PresentationSheet() {
  const { profile } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [selected, setSelected] = useState({}); // sku -> true
  const [promos, setPromos] = useState({});     // sku -> { type, pct, price, n, m }

  const [customerName, setCustomerName] = useState("");
  const [sheetDate, setSheetDate] = useState(todayISO());
  const [repName, setRepName] = useState(profile?.name || "");
  const [hidePrices, setHidePrices] = useState(false);
  const [promoUntil, setPromoUntil] = useState(""); // printed on every page

  useEffect(() => {
    async function load() {
      const all = await getAllProducts(); // session cache — 0 reads on revisit
      setProducts(all.filter(p => p.active).sort(skuCompare));
      setLoading(false);
    }
    load();
  }, []);

  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();

  const filtered = products.filter(p => {
    const matchesSearch = productMatches(p, search); // punctuation-blind
    const matchesBrand = brandFilter === "all" || p.brand === brandFilter;
    return matchesSearch && matchesBrand;
  });

  const selectedProducts = products.filter(p => selected[p.sku]).sort(sheetOrder);

  // 9 per printed page, always — hard page breaks between chunks.
  const pages = [];
  for (let i = 0; i < selectedProducts.length; i += PER_PAGE) {
    pages.push(selectedProducts.slice(i, i + PER_PAGE));
  }


  function toggle(sku) {
    setSelected(prev => {
      const next = { ...prev };
      if (next[sku]) delete next[sku];
      else next[sku] = true;
      return next;
    });
  }

  // Click-and-drag selection (Brian, 2026-08-21): press on a row and sweep
  // down — every row passed takes the state the FIRST row flipped to (drag
  // over selected rows while starting on an unselected one still selects).
  // A plain click is just a one-row drag. Promo controls are excluded so
  // typing a discount never starts a drag.
  const dragSetTo = useRef(null);

  useEffect(() => {
    const end = () => { dragSetTo.current = null; };
    window.addEventListener("mouseup", end);
    return () => window.removeEventListener("mouseup", end);
  }, []);

  function setSku(sku, on) {
    setSelected(prev => {
      if (!!prev[sku] === on) return prev;
      const next = { ...prev };
      if (on) next[sku] = true;
      else delete next[sku];
      return next;
    });
  }

  function rowMouseDown(p, e) {
    if (e.button !== 0) return; // left button only
    if (e.target.closest(".pick-promo")) return; // promo editing, not selection
    e.preventDefault(); // no text-selection while sweeping
    const setTo = !selected[p.sku];
    dragSetTo.current = setTo;
    setSku(p.sku, setTo);
  }

  function rowMouseEnter(p) {
    if (dragSetTo.current === null) return;
    setSku(p.sku, dragSetTo.current);
  }

  function setPromo(sku, patch) {
    setPromos(prev => {
      const cur = prev[sku] || {};
      const next = { ...cur, ...patch };
      if (!next.type) {
        const rest = { ...prev };
        delete rest[sku];
        return rest;
      }
      return { ...prev, [sku]: next };
    });
  }

  // Selects everything currently SHOWN (respects search + brand filter),
  // merging into any existing selection.
  function selectAllFiltered() {
    setSelected(prev => {
      const next = { ...prev };
      filtered.forEach(p => { next[p.sku] = true; });
      return next;
    });
  }

  if (loading) return <p>Loading products...</p>;

  return (
    <div className="presentation-page">
      <div className="presentation-builder no-print">
        <h2>Presentation Sheet</h2>

        <div className="sheet-header-inputs">
          <label>
            Customer name
            <input
              placeholder="e.g. H-Mart Coquitlam"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </label>
          <label>
            Date
            <input
              type="date"
              value={sheetDate}
              onChange={(e) => setSheetDate(e.target.value)}
            />
          </label>
          <label>
            Rep name
            <input
              placeholder="Rep name"
              value={repName}
              onChange={(e) => setRepName(e.target.value)}
            />
          </label>
          <label>
            Promos effective until
            <input
              type="date"
              value={promoUntil}
              onChange={(e) => setPromoUntil(e.target.value)}
              title="Prints small at the bottom of every page when set"
            />
          </label>
        </div>

        <div className="sheet-print-controls">
          <label className="hide-prices-toggle">
            <input
              type="checkbox"
              checked={hidePrices}
              onChange={(e) => setHidePrices(e.target.checked)}
            />
            Hide prices on this sheet
          </label>
          <span className="selected-count">Selected: {selectedProducts.length} ({pages.length || 0} page{pages.length !== 1 ? "s" : ""})</span>
          <button
            className="clear-selection-btn"
            disabled={filtered.length === 0}
            onClick={selectAllFiltered}
          >
            Select all ({filtered.length})
          </button>
          <button
            className="clear-selection-btn"
            disabled={selectedProducts.length === 0}
            onClick={() => setSelected({})}
          >
            Clear all
          </button>
          <button
            className="print-btn"
            disabled={selectedProducts.length === 0}
            onClick={() => window.print()}
          >
            🖨 Print (Ctrl+P)
          </button>
        </div>

        <div className="presentation-controls">
          <input
            placeholder="Search by name, brand, or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
            <option value="all">All Brands</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        <div className="presentation-pick-list">
          {filtered.map(p => {
            const promo = promos[p.sku];
            const sale = salePrice(p, promo);
            return (
              <div
                key={p.sku}
                className="pick-row"
                onMouseDown={(e) => rowMouseDown(p, e)}
                onMouseEnter={() => rowMouseEnter(p)}
              >
                <input
                  type="checkbox"
                  checked={!!selected[p.sku]}
                  readOnly
                  style={{ pointerEvents: "none" }}
                />
                {p.imageUrl
                  ? <img src={p.imageUrl} alt="" className="pick-thumb" loading="lazy" />
                  : <span className="pick-thumb pick-thumb-empty" />}
                <span className="pick-sku">{p.sku}</span>
                <span className="pick-name">{p.name}</span>
                {/* Promo controls live on every row, left of the regular
                    price (Brian, 2026-08-21). Picking a promo type on an
                    unselected row also selects it — a promo you configure
                    is a promo you mean to print. */}
                <span className="pick-promo" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={promo?.type || ""}
                    onChange={(e) => {
                      setPromo(p.sku, { type: e.target.value });
                      if (e.target.value && !selected[p.sku]) toggle(p.sku);
                    }}
                  >
                    <option value="">No promo</option>
                    <option value="pct">% off</option>
                    <option value="price">Sale price $</option>
                    <option value="nm">N + M free</option>
                  </select>
                  {promo?.type === "pct" && (
                    <span className="pick-promo-inputs">
                      <input
                        type="number" min="1" max="99" placeholder="10"
                        value={promo.pct ?? ""}
                        onChange={(e) => setPromo(p.sku, { pct: e.target.value })}
                      />%
                      {sale != null && <em>→ ${sale.toFixed(2)}</em>}
                    </span>
                  )}
                  {promo?.type === "price" && (
                    <span className="pick-promo-inputs">
                      $<input
                        type="number" min="0.01" step="0.01" placeholder={p.basePrice.toFixed(2)}
                        value={promo.price ?? ""}
                        onChange={(e) => setPromo(p.sku, { price: e.target.value })}
                      />
                      {sale != null && sale >= p.basePrice && <em className="error">≥ regular!</em>}
                    </span>
                  )}
                  {promo?.type === "nm" && (
                    <span className="pick-promo-inputs">
                      <input
                        type="number" min="1" placeholder="10"
                        value={promo.n ?? ""}
                        onChange={(e) => setPromo(p.sku, { n: e.target.value })}
                      />+<input
                        type="number" min="1" placeholder="1"
                        value={promo.m ?? ""}
                        onChange={(e) => setPromo(p.sku, { m: e.target.value })}
                      /> free
                    </span>
                  )}
                </span>
                <span className="pick-price">${p.basePrice.toFixed(2)}</span>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="pick-empty">No products match your search.</p>}
        </div>

        <h3 className="preview-heading">Print preview — this is exactly what prints:</h3>
      </div>

      <div className="sheet">
        {selectedProducts.length === 0 ? (
          <p className="sheet-empty no-print">Select products above to build the sheet.</p>
        ) : (
          pages.map((chunk, pi) => (
            <div key={pi} className={`sheet-page ${pi === 0 ? "sheet-page-first" : "sheet-page-rest"}`}>
              {pi === 0 && (
                <div className="sheet-header">
                  <h1>Sunjin Trading Inc.</h1>
                  <div className="sheet-header-fields">
                    {customerName && <p><strong>Prepared for:</strong> {customerName}</p>}
                    <p><strong>Date:</strong> {formatSheetDate(sheetDate)}</p>
                    {repName && <p><strong>Rep:</strong> {repName}</p>}
                  </div>
                </div>
              )}
              <div className="sheet-grid">
                {chunk.map(p => {
                  const promo = promos[p.sku];
                  const sale = salePrice(p, promo);
                  const deal = nmLabel(promo);
                  return (
                    <div key={p.sku} className="sheet-card">
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt={p.name} />
                        : <div className="sheet-card-noimage">No photo</div>}
                      <p className="sheet-card-sku">#{p.sku}</p>
                      {p.barcode && <p className="sheet-card-barcode">Barcode: {p.barcode}</p>}
                      <p className="sheet-card-name">{p.name}</p>
                      {!hidePrices && (
                        sale != null ? (
                          <p className="sheet-card-price">
                            <s className="sheet-card-was">${p.basePrice.toFixed(2)}</s>{" "}
                            <span className="sheet-card-sale">
                              ${sale.toFixed(2)}{promo.type === "pct" ? ` (${parseFloat(promo.pct)}% off)` : ""}
                            </span>
                          </p>
                        ) : (
                          <p className="sheet-card-price">
                            ${p.basePrice.toFixed(2)} CAD
                            {deal && <span className="sheet-card-sale"> · {deal}</span>}
                          </p>
                        )
                      )}
                      {hidePrices && deal && (
                        <p className="sheet-card-price"><span className="sheet-card-sale">{deal}</span></p>
                      )}
                      <p className="sheet-card-storage">
                        {p.storageMethod || "—"}
                      </p>
                    </div>
                  );
                })}
              </div>
              {/* Date set = footer prints on every page, full stop (an
                  earlier and-a-promo-exists condition made it vanish). */}
              {promoUntil && (
                <p className="sheet-page-footer">
                  * Promotional pricing effective until {formatSheetDate(promoUntil)}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
